import React, { useState, useEffect, useCallback } from "react";
import MoonLoader from "react-spinners/MoonLoader";
import { useCRM } from "../CRMContext";
import {
  getDashboardSummary,
  getDashboardFunnel,
  getDashboardByPeriod,
  getDashboardByOwner,
  getDashboardLossReasons,
  getDashboardActivities,
} from "../services/dashboard.service";

const formatCurrency = (value) => {
  if (!value && value !== 0) return "R$ 0";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 }).format(value);
};

const PERIODS = [
  { value: 7, label: "7 dias" },
  { value: 15, label: "15 dias" },
  { value: 30, label: "30 dias" },
  { value: 60, label: "60 dias" },
  { value: 90, label: "90 dias" },
];

const Dashboard = () => {
  const { state } = useCRM();
  const pipelineId = state.activePipelineId;

  const [period, setPeriod] = useState(30);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [byPeriod, setByPeriod] = useState(null);
  const [byOwner, setByOwner] = useState([]);
  const [lossReasons, setLossReasons] = useState(null);
  const [activitiesMetrics, setActivitiesMetrics] = useState(null);

  const loadAll = useCallback(async () => {
    if (!pipelineId) return;
    setLoading(true);
    try {
      const [s, f, p, o, l, a] = await Promise.all([
        getDashboardSummary(pipelineId, period),
        getDashboardFunnel(pipelineId),
        getDashboardByPeriod(pipelineId, period),
        getDashboardByOwner(pipelineId, period),
        getDashboardLossReasons(pipelineId, period),
        getDashboardActivities(pipelineId, period),
      ]);
      setSummary(s);
      setFunnel(f || []);
      setByPeriod(p);
      setByOwner(o?.results || []);
      setLossReasons(l);
      setActivitiesMetrics(a);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [pipelineId, period]);

  useEffect(() => { loadAll(); }, [loadAll]);

  if (loading) {
    return <div className="crm-loading"><MoonLoader color="#3b82f6" size={30} /></div>;
  }

  const maxFunnelCount = Math.max(...funnel.map((f) => f.count), 1);
  const maxOwnerValue = Math.max(...byOwner.map((o) => o.total_value || 0), 1);

  return (
    <div className="crm-dashboard">
      {/* Period selector */}
      <div className="crm-dashboard__period-selector">
        {PERIODS.map((p) => (
          <button
            key={p.value}
            className={`crm-dashboard__period-btn ${period === p.value ? "crm-dashboard__period-btn--active" : ""}`}
            onClick={() => setPeriod(p.value)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* KPI Cards */}
      {summary && (
        <div className="crm-dashboard__kpi-grid">
          <div className="crm-dashboard__kpi">
            <div className="crm-dashboard__kpi-value">{summary.total_open}</div>
            <div className="crm-dashboard__kpi-label">Negócios Abertos</div>
          </div>
          <div className="crm-dashboard__kpi">
            <div className="crm-dashboard__kpi-value">{formatCurrency(summary.total_value)}</div>
            <div className="crm-dashboard__kpi-label">Valor Total</div>
          </div>
          <div className="crm-dashboard__kpi">
            <div className="crm-dashboard__kpi-value">{formatCurrency(summary.weighted_value)}</div>
            <div className="crm-dashboard__kpi-label">Valor Ponderado</div>
          </div>
          <div className="crm-dashboard__kpi">
            <div className="crm-dashboard__kpi-value">{summary.won_count}</div>
            <div className="crm-dashboard__kpi-label">Ganhos</div>
          </div>
          <div className="crm-dashboard__kpi">
            <div className="crm-dashboard__kpi-value">{summary.conversion_rate}%</div>
            <div className="crm-dashboard__kpi-label">Taxa de Conversão</div>
          </div>
          <div className="crm-dashboard__kpi">
            <div className="crm-dashboard__kpi-value">{formatCurrency(summary.avg_ticket)}</div>
            <div className="crm-dashboard__kpi-label">Ticket Médio</div>
          </div>
          <div className="crm-dashboard__kpi">
            <div className="crm-dashboard__kpi-value">{summary.avg_cycle_days ?? "—"}</div>
            <div className="crm-dashboard__kpi-label">Ciclo Médio (dias)</div>
          </div>
        </div>
      )}

      {/* Funnel */}
      {funnel.length > 0 && (
        <div className="crm-dashboard__chart">
          <h4 className="crm-dashboard__chart-title">Funil de Conversão</h4>
          {funnel.map((stage) => (
            <div key={stage.column_id} className="crm-dashboard__funnel-bar">
              <span className="crm-dashboard__funnel-label">{stage.column_name}</span>
              <div className="crm-dashboard__funnel-track">
                <div
                  className="crm-dashboard__funnel-fill"
                  style={{
                    width: `${Math.max((stage.count / maxFunnelCount) * 100, 8)}%`,
                    background: stage.color || "#3b82f6",
                  }}
                >
                  {formatCurrency(stage.total_value)}
                </div>
              </div>
              <span className="crm-dashboard__funnel-count">{stage.count}</span>
            </div>
          ))}
        </div>
      )}

      {/* By Period */}
      {byPeriod && (
        <div className="crm-dashboard__chart">
          <h4 className="crm-dashboard__chart-title">Negócios por Período</h4>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-muted)", marginBottom: 8 }}>Criados</div>
              {(byPeriod.created || []).map((item, idx) => (
                <div key={idx} style={{ fontSize: 13, padding: "4px 0", borderBottom: "1px solid var(--border-color)" }}>
                  <span style={{ color: "var(--text-primary)" }}>{item.period}</span>
                  <span style={{ float: "right", fontWeight: 600 }}>{item.count}</span>
                </div>
              ))}
              {(!byPeriod.created || byPeriod.created.length === 0) && (
                <span style={{ fontSize: 13, color: "var(--text-muted)" }}>Sem dados</span>
              )}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--success)", marginBottom: 8 }}>Ganhos</div>
              {(byPeriod.won || []).map((item, idx) => (
                <div key={idx} style={{ fontSize: 13, padding: "4px 0", borderBottom: "1px solid var(--border-color)" }}>
                  <span style={{ color: "var(--text-primary)" }}>{item.period}</span>
                  <span style={{ float: "right", fontWeight: 600, color: "var(--success)" }}>{item.count}</span>
                </div>
              ))}
              {(!byPeriod.won || byPeriod.won.length === 0) && (
                <span style={{ fontSize: 13, color: "var(--text-muted)" }}>Sem dados</span>
              )}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--danger)", marginBottom: 8 }}>Perdidos</div>
              {(byPeriod.lost || []).map((item, idx) => (
                <div key={idx} style={{ fontSize: 13, padding: "4px 0", borderBottom: "1px solid var(--border-color)" }}>
                  <span style={{ color: "var(--text-primary)" }}>{item.period}</span>
                  <span style={{ float: "right", fontWeight: 600, color: "var(--danger)" }}>{item.count}</span>
                </div>
              ))}
              {(!byPeriod.lost || byPeriod.lost.length === 0) && (
                <span style={{ fontSize: 13, color: "var(--text-muted)" }}>Sem dados</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* By Owner */}
      {byOwner.length > 0 && (
        <div className="crm-dashboard__chart">
          <h4 className="crm-dashboard__chart-title">Distribuição por Responsável</h4>
          {byOwner.map((owner, idx) => {
            const name = `${owner.owner__first_name || ""} ${owner.owner__last_name || ""}`.trim() || "Sem responsável";
            return (
              <div key={idx} className="crm-dashboard__owner-row">
                <span className="crm-dashboard__owner-name">{name}</span>
                <div className="crm-dashboard__owner-bar">
                  <div
                    className="crm-dashboard__owner-fill"
                    style={{ width: `${Math.max((owner.total_value / maxOwnerValue) * 100, 4)}%` }}
                  />
                </div>
                <span className="crm-dashboard__owner-value">{owner.total_deals} deals</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Loss Reasons */}
      {lossReasons && lossReasons.reasons && lossReasons.reasons.length > 0 && (
        <div className="crm-dashboard__chart">
          <h4 className="crm-dashboard__chart-title">Motivos de Perda</h4>
          {lossReasons.reasons.map((reason, idx) => {
            const pct = lossReasons.total_lost > 0
              ? Math.round((reason.count / lossReasons.total_lost) * 100)
              : 0;
            return (
              <div key={idx} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border-color)" }}>
                <span style={{ fontSize: 14, color: "var(--text-primary)" }}>{reason.loss_reason || "Não informado"}</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-muted)" }}>{reason.count} ({pct}%)</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Activities Metrics */}
      {activitiesMetrics && (
        <div className="crm-dashboard__chart">
          <h4 className="crm-dashboard__chart-title">Métricas de Atividades</h4>
          <div className="crm-dashboard__kpi-grid" style={{ marginBottom: 0 }}>
            <div className="crm-dashboard__kpi">
              <div className="crm-dashboard__kpi-value">{activitiesMetrics.total}</div>
              <div className="crm-dashboard__kpi-label">Total</div>
            </div>
            <div className="crm-dashboard__kpi">
              <div className="crm-dashboard__kpi-value">{activitiesMetrics.completed}</div>
              <div className="crm-dashboard__kpi-label">Concluídas</div>
            </div>
            <div className="crm-dashboard__kpi">
              <div className="crm-dashboard__kpi-value">{activitiesMetrics.overdue}</div>
              <div className="crm-dashboard__kpi-label">Atrasadas</div>
            </div>
            <div className="crm-dashboard__kpi">
              <div className="crm-dashboard__kpi-value">{activitiesMetrics.completion_rate}%</div>
              <div className="crm-dashboard__kpi-label">Taxa de Conclusão</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
